import {
  useState
} from "react";
import {
  useNavigate,
  Link
} from "react-router-dom";
import {
  sendOtp
} from "../api/emailAuthApi";
import {
  ArrowRight,
  ArrowLeft,
  GraduationCap
} from "lucide-react";

import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Input
} from "../components/ui";
import {
  useToast
} from "../context/ToastContext";

const Signup = () => {
  const navigate = useNavigate();

  // Multi-step view tracking state
  const [currentStep, setCurrentStep] = useState(1);

  const toast = useToast();

  // Functional States
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [agreeTerms, setAgreeTerms] = useState(false);
  
  // Interface Operations States
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  // Step Navigation Validation Handler
  const handleNextStep = async () => {
    setValidationErrors({});
    let errors = {};

    if (currentStep === 1) {
      if (!name.trim()) errors.name = "Please enter your full name.";
      if (!agreeTerms) errors.terms = "You must agree to the Terms of Service.";
      
      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        return;
      }
      setCurrentStep(2);
    }

    if (currentStep === 2) {
      if (!email.includes("@")) {
        errors.email = "Please enter a valid institutional email.";
        setValidationErrors(errors);
        return;
      }

      try {
        setLoading(true);
        await sendOtp(email);
        setCurrentStep(3);
      } catch (err) {
        toast.error(
          err?.response?.data?.message || "Failed to validate email. Please check your connection."
        );
      } finally {
        setLoading(false);
      }
    }
  };

  const handlePrevStep = () => {
    setValidationErrors({});
    setCurrentStep((prev) => prev - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidationErrors({});

    let errors = {};
    if (!email.includes("@")) {
      errors.email = "Please enter a valid institutional email.";
    }
    if (password !== confirmPassword) {
      errors.password = "Passwords do not match.";
    }
    if (!agreeTerms) {
      errors.terms = "You must agree to the Terms of Service.";
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    navigate("/verify-otp", {
      state: {
        name,
        email,
        password,
      },
    });
  };

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-950 flex flex-col justify-between antialiased">
      {/* Header */}
      <header className="w-full h-14 bg-white dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-800 flex items-center px-4 sm:px-8">
        <div className="flex items-center gap-2">
          <GraduationCap className="text-neutral-900 dark:text-neutral-100 w-6 h-6" />
          <span className="text-base font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">
            EduClarify ERP
          </span>
        </div>
      </header>

      {/* Main Container */}
      <main className="w-full max-w-md mx-auto my-auto p-4 sm:p-6">
        <Card className="border border-neutral-200 dark:border-neutral-800 shadow-none bg-white dark:bg-neutral-900">
          <CardHeader className="pt-6 pb-4 border-b border-neutral-100 dark:border-neutral-800">
            {/* Progress Bar */}
            <div className="mb-4">
              <div className="flex items-center justify-between text-xs mb-1.5">
                <span className="font-semibold text-neutral-800 dark:text-neutral-100">Account Setup</span>
                <span className="text-neutral-400 dark:text-neutral-500">Step {currentStep} of 3</span>
              </div>
              <div className="h-1.5 w-full bg-neutral-100 dark:bg-neutral-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-neutral-900 rounded-full transition-all duration-300"
                  style={{ width: `${currentStep * 33.34}%` }}
                />
              </div>
            </div>

            <CardTitle className="text-lg font-bold text-neutral-900 dark:text-neutral-100">
              Create your account
            </CardTitle>
            <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-0.5">
              {currentStep === 1 && "Start by filling out your personal details."}
              {currentStep === 2 && "Enter your valid institutional email address."}
              {currentStep === 3 && "Secure your profile with a new password."}
            </p>
          </CardHeader>

          <CardContent className="p-6 space-y-4">
            

            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              {/* STEP 1 */}
              {currentStep === 1 && (
                <div className="space-y-3">
                  <Input
                    label="Full Name *"
                    type="text"
                    placeholder="Dr. Sarah Johnson"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    error={validationErrors.name}
                    required
                  />

                  <div className="pt-1">
                    <label className="flex items-start gap-2 text-xs text-neutral-600 dark:text-neutral-300 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={agreeTerms}
                        onChange={(e) => setAgreeTerms(e.target.checked)}
                        className="rounded border-neutral-300 dark:border-neutral-700 text-neutral-900 dark:text-neutral-100 focus:ring-neutral-900 mt-0.5"
                      />
                      <span>
                        I agree to the{" "}
                        <Link to="/terms" className="text-neutral-900 dark:text-neutral-100 hover:underline font-semibold">
                          Terms of Service
                        </Link>{" "}
                        and{" "}
                        <Link to="/privacy" className="text-neutral-900 dark:text-neutral-100 hover:underline font-semibold">
                          Privacy Policy
                        </Link>.
                      </span>
                    </label>
                    {validationErrors.terms && (
                      <p className="text-[11px] text-danger-600 dark:text-danger-500 mt-1">
                        {validationErrors.terms}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* STEP 2 */}
              {currentStep === 2 && (
                <div className="space-y-3">
                  <Input
                    label="Email Address *"
                    type="email"
                    placeholder="sarah.j@oakwood.edu"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    error={validationErrors.email}
                    disabled={loading}
                    required
                  />
                </div>
              )}

              {/* STEP 3 */}
              {currentStep === 3 && (
                <div className="space-y-3">
                  <Input
                    label="Password *"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    error={validationErrors.password}
                    required
                  />

                  <Input
                    label="Confirm Password *"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    error={validationErrors.password}
                    required
                  />
                </div>
              )}

              {/* Navigation Actions */}
              <div className="flex gap-2 pt-2">
                {currentStep > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handlePrevStep}
                    disabled={loading}
                    leftIcon={<ArrowLeft className="w-3.5 h-3.5" />}
                    className="w-1/3 text-xs border border-neutral-200 dark:border-neutral-800 py-2.5"
                  >
                    Back
                  </Button>
                )}

                {currentStep < 3 ? (
                  <Button
                    type="button"
                    variant="primary"
                    onClick={handleNextStep}
                    isLoading={loading}
                    rightIcon={<ArrowRight className="w-3.5 h-3.5" />}
                    className="flex-1 text-xs font-semibold py-2.5"
                  >
                    Continue
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    variant="primary"
                    rightIcon={<ArrowRight className="w-3.5 h-3.5" />}
                    className="flex-1 text-xs font-semibold py-2.5"
                  >
                    Complete Setup
                  </Button>
                )}
              </div>
            </form>

            <div className="pt-4 border-t border-neutral-100 dark:border-neutral-800 text-center">
              <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">
                Already have an account?{" "}
                <Link to="/login" className="text-neutral-900 dark:text-neutral-100 font-bold hover:underline">
                  Sign in instead
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Footer */}
      <footer className="py-4 text-center">
        <p className="text-[11px] text-neutral-400 dark:text-neutral-500">
          © {new Date().getFullYear()} EduClarify ERP System. All rights reserved.
        </p>
      </footer>
    </div>
  );
};

export default Signup;